import type { DocumentRegion } from './document-region';
/**
 * AI Gateway — server-side AI via the Trace login.
 *
 * Every AI call goes to the llm-gateway (VITE_AI_GATEWAY_URL), which validates
 * the catalog JWT, holds the provider keys and runs the assistant's loop
 * (hosted-session.ts). The editor holds no key and calls no provider.
 *
 * Design and rollout plan: ml-services/llm-gateway/DESIGN.md.
 */

import {
    ensureFreshToken,
    getCatalogSelectedTeamId,
    getCatalogToken,
    onCatalogTokenChange,
} from '../catalog-auth';
import { refreshCredits } from './credits';
import { parseRetryAfter, whileRateLimited } from './rate-limit-wait';

const rawUrl = import.meta.env.VITE_AI_GATEWAY_URL as string | undefined;

/** Gateway base URL without trailing slash; null when not configured. */
export const AI_GATEWAY_URL: string | null = rawUrl ? rawUrl.replace(/\/+$/, '') : null;

export function isGatewayConfigured(): boolean {
    return AI_GATEWAY_URL !== null;
}

/** True when gateway calls can be attempted right now (configured + signed in). */
export function isGatewayAvailable(): boolean {
    return isGatewayConfigured() && getCatalogToken() !== null;
}

/**
 * Fresh catalog JWT for a gateway call, or null (no gateway / signed out /
 * refresh failed). Cheap when the cached token is far from expiry.
 */
export async function getGatewayToken(): Promise<string | null> {
    if (!isGatewayConfigured()) return null;
    return ensureFreshToken();
}

/**
 * The team AI usage is billed to — the catalog's selected team.
 *
 * The gateway treats this as a *claim*, not an authorization: it settles
 * against the CMS with the caller's own JWT, so CMS membership is what
 * actually decides whether the debit lands. Sending it wrong costs the user
 * a clear error, not someone else's credits.
 */
export function getBillingTeamId(): string | null {
    return getCatalogSelectedTeamId();
}

/** Auth + billing headers for a gateway call, given an already-resolved token. */
function gatewayHeadersFor(token: string | null): Record<string, string> {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const teamId = getBillingTeamId();
    if (teamId) headers['x-trace-team-id'] = teamId;
    return headers;
}

/** Fresh auth and billing headers for a gateway request: what each hosted-session request sends. */
export async function getGatewayHeaders(): Promise<Record<string, string>> {
    return gatewayHeadersFor(await getGatewayToken());
}

/**
 * fetch that injects a fresh bearer per request, for every direct gateway
 * call. A 429 is waited out here (rate-limit-wait.ts).
 */
export const gatewayFetch: typeof fetch = async (input, init) => {
    const request = input instanceof Request ? input : null;
    const replayable = !(init?.body instanceof ReadableStream) && !request?.body;
    const res = await whileRateLimited(async () => {
        const headers = new Headers(init?.headers);
        for (const [name, value] of Object.entries(gatewayHeadersFor(await getGatewayToken()))) {
            headers.set(name, value);
        }
        return fetch(input, { ...init, headers });
    }, outcome => ('value' in outcome && outcome.value.status === 429 && replayable
        ? parseRetryAfter(outcome.value.headers.get('retry-after'))
        : undefined), init?.signal ?? request?.signal);
    return res;
};

// ── Gateway deadlines and errors ────────────────────────────

/**
 * The longest one gateway operation may take: the 3D job poller's ceiling
 * (generateModel3d below). A generation is the slowest thing the gateway
 * does; it measured 1–3 minutes when generate_model landed, and the poller
 * has allowed well over twice that ever since.
 */
export const GATEWAY_OPERATION_TIMEOUT_MS = 8 * 60_000;

/**
 * How long a discovery request (/v1/routing) may
 * take, body included. The gateway answers these from memory; the one step
 * that can wait is verifying the sign-in, and the gateway gives up on that
 * JWKS read at 10 s (llm-gateway src/auth.ts JWKS_FETCH_TIMEOUT_MS). Twice
 * that: by then the gateway has answered or given up, so the request is lost.
 *
 * Every turn awaits the one in-flight discovery, so a request that never
 * finished used to stall every later send for the rest of the session.
 */
export const DISCOVERY_TIMEOUT_MS = 20_000;

/** The abort a deadline raises: a TimeoutError, never a Stop's AbortError. */
export function timeoutAbort(what: string): DOMException {
    return new DOMException(`${what} timed out`, 'TimeoutError');
}

/** True for an abort raised by a deadline rather than by the user. */
export function isTimeoutAbort(err: unknown): boolean {
    return err instanceof DOMException && err.name === 'TimeoutError';
}

/** One discovery GET; the deadline covers the body, not just the headers. */
async function discoveryGet<T>(path: string): Promise<{ status: number; data: T | null }> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(timeoutAbort(`GET ${path}`)), DISCOVERY_TIMEOUT_MS);
    try {
        const res = await gatewayFetch(`${AI_GATEWAY_URL}${path}`, { signal: controller.signal });
        return { status: res.status, data: res.ok ? await res.json() as T : null };
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Whether an error carries this llm-gateway refusal code. The gateway refuses
 * with `{ error: '<code>', message }`: a SessionError keeps the code on `.code`,
 * and a capability call's `Gateway <status>: <body>` error has the body in its
 * message text.
 */
function hasGatewayCode(err: unknown, message: string, code: string): boolean {
    return (err as { code?: unknown } | null)?.code === code || message.includes(code);
}

/**
 * Rewrite a gateway billing refusal into something a creator can act on.
 * Returns null for anything else, so callers fall back to the raw error.
 *
 * It reads the status field or the message for an HTTP 402, and the gateway's
 * own error code wherever hasGatewayCode finds it.
 */
export function describeBillingError(err: unknown): string | null {
    const status = (err as { status?: unknown })?.status;
    const message = err instanceof Error ? err.message : String(err);
    const is402 = status === 402 || /\b402\b/.test(message);
    const noTeam = hasGatewayCode(err, message, 'no_billing_team');
    const noCredits = hasGatewayCode(err, message, 'insufficient_credits');
    if (!is402 && !noTeam && !noCredits) return null;

    if (noTeam) {
        return 'AI usage is billed to a team, but no team is selected. Pick one in the catalog panel and try again.';
    }
    if (noCredits) {
        return 'This team is out of AI credits. Top up in Billing to keep going.';
    }
    return is402 ? 'AI usage was refused for billing reasons. Check the team\'s credits in Billing.' : null;
}

/** Where the assistant was trying to reach, for a "can't connect" message. */
function gatewayHostLabel(): string {
    if (!AI_GATEWAY_URL) return 'Trace AI';
    try {
        return new URL(AI_GATEWAY_URL).host;
    } catch {
        return AI_GATEWAY_URL;
    }
}

/**
 * The HTTP status an error carries on `status` (SessionError does). Nothing
 * else is parsed: a status read out of a response body would misfile a 400
 * that happens to quote a 5xx.
 */
function providerStatus(err: unknown): number | undefined {
    const status = (err as { status?: unknown })?.status;
    return typeof status === 'number' ? status : undefined;
}

/**
 * A request that never reached the gateway. Only the runtime's own wording
 * counts — Chrome "Failed to fetch", Firefox "NetworkError…", Safari "Load
 * failed", Node "fetch failed" (all TypeErrors) — so a TypeError from a bug in
 * our code is never reported as the network.
 */
function isNetworkFailure(err: unknown, message: string): boolean {
    return err instanceof TypeError && /Failed to fetch|NetworkError|Load failed|fetch failed/i.test(message);
}

/**
 * What a 403 from the gateway means. Unlike a 401 the token is fine, so
 * signing in again changes nothing (auth.ts in llm-gateway says the same:
 * clients must not treat a 403 as expiry). The gateway names the reason with
 * the same kind of code as the billing refusals.
 */
function describeForbidden(err: unknown, message: string): string {
    if (hasGatewayCode(err, message, 'guest_not_permitted')) {
        return 'Trace AI needs a signed-in Trace account, and this is a guest session. Sign in from the catalog panel, then try again.';
    }
    if (hasGatewayCode(err, message, 'model_not_allowed')) {
        return 'Trace AI does not allow that model on this deployment.';
    }
    return 'Trace AI refused this request (403). That is not a sign-in problem, so signing in again will not change it. Check the team\'s plan and limits.';
}

/**
 * One plain sentence for anything a gateway call can fail with: what
 * happened, and what the user can do about it. Before this the chat printed
 * whatever the error said, raw JSON body included.
 *
 * The retry advice says "try again", not "send the message again": the same
 * text answers a chat turn and a click in a panel.
 */
export function describeGatewayError(err: unknown): string {
    const billing = describeBillingError(err);
    if (billing) return billing;

    const message = err instanceof Error ? err.message : String(err);
    const status = providerStatus(err);

    if (isTimeoutAbort(err)) {
        const minutes = Math.round(GATEWAY_OPERATION_TIMEOUT_MS / 60_000);
        return `Trace AI stopped responding for ${minutes} minutes, so the request was dropped. Try again; if it keeps happening, the gateway is stuck.`;
    }
    if (status === 401) {
        return 'Trace AI did not accept your sign-in. Sign in again from the catalog panel, then try again.';
    }
    if (status === 403) return describeForbidden(err, message);
    if (isNetworkFailure(err, message)) {
        return `Could not reach Trace AI at ${gatewayHostLabel()}. Check that you are online (or that the local gateway is running), then try again.`;
    }
    if (status === 429) {
        return 'Trace AI is rate limited right now. Wait a minute and try again.';
    }
    if (status !== undefined && status >= 500) {
        return `Trace AI had a server error (${status}) and could not answer. Try again in a moment.`;
    }
    // Anything else keeps the error's own text: a raw line beats a wrong diagnosis.
    return message || 'Trace AI could not answer this request.';
}

// ── Server-side routing (/v1/routing) ───────────────────────
// The editor names a *role* for its capability calls (speech, images,
// vision, 3D, materials), never a model; the gateway resolves it. The routing
// table also carries each role's label, which the tool chips show, and the
// PDF preview limit fetch_url reads.

/** Jobs the editor asks a model to do. Mirrors the gateway's ROLES. */
export type AIRole =
    | 'stt' | 'tts' | 'image' | 'vision'
    /** 3D model generation — the only role whose work outlives the request. */
    | 'model3d'
    /** PBR material generation — prompt in, textured material GLB out. */
    | 'material';

export interface GatewayRoute {
    role: AIRole;
    /** The id to send in place of a model. */
    alias: string;
    /**
     * `trace` is the gateway's own normalized format: the role is served by
     * POSTing one fixed body to /v1/speech, /v1/images or /v1/vision, and which
     * provider answers is not the client's business. See callCapability below.
     */
    surface: string;
    /** What to show the user in place of a model name. */
    label: string;
    /**
     * The deployment currently answering the role. Optional because a gateway
     * older than 2026-09-04 publishes routes without it — the settings panel
     * degrades to "Trace AI" with no model named rather than showing
     * "undefined".
     */
    model?: string;
}

let gatewayRoutes: Map<AIRole, GatewayRoute> | null = null;
/** Set once the discovery endpoints have answered for the current sign-in. */
let discovered = false;
let modelsFetch: Promise<void> | null = null;
const modelsListeners = new Set<() => void>();

/** The gateway's route for a role, or null before the table answered or when this deploy serves none. */
export function getGatewayRoute(role: AIRole): GatewayRoute | null {
    if (!hasGatewayRouting()) return null;
    return gatewayRoutes?.get(role) ?? null;
}

/** Whether the routing table has answered for this sign-in. */
export function hasGatewayRouting(): boolean {
    return isGatewayAvailable() && (gatewayRoutes?.size ?? 0) > 0;
}

/** Subscribe to routing-table updates (UI re-render). Returns unsubscribe. */
export function onGatewayModelsChange(listener: () => void): () => void {
    modelsListeners.add(listener);
    return () => modelsListeners.delete(listener);
}

/** Fetch the gateway's routing table; coalesces concurrent calls. */
export function refreshGatewayModels(): Promise<void> {
    if (!isGatewayAvailable()) return Promise.resolve();
    modelsFetch ??= (async () => {
        try {
            const routingRes = await discoveryGet<{ routes?: GatewayRoute[] }>('/v1/routing');
            if (routingRes.data) {
                const data = routingRes.data;
                if (Array.isArray(data.routes)) gatewayRoutes = new Map(data.routes.map(r => [r.role, r]));
            } else {
                console.warn(`[AI] gateway /v1/routing returned ${routingRes.status}`);
            }
            // An auth failure is "this sign-in does not work here": the next sign-in asks again.
            discovered = routingRes.status !== 401 && routingRes.status !== 403;
            for (const l of modelsListeners) l();
        } catch (err) {
            // Leaves `discovered` false, so the next call retries.
            console.warn('[AI] gateway discovery fetch failed:', err);
        } finally {
            modelsFetch = null;
        }
    })();
    return modelsFetch;
}

/** Resolve once this sign-in's routing table is known — cheap after the first success. */
export function ensureGatewayDiscovery(): Promise<void> {
    if (discovered || !isGatewayAvailable()) return Promise.resolve();
    return refreshGatewayModels();
}

// Discover models at startup and on sign-in/sign-out. A failed fetch leaves
// the optimistic default in place; the next token change retries.
if (isGatewayConfigured()) {
    void refreshGatewayModels();
    onCatalogTokenChange(() => {
        // A different account may see a different gateway answer.
        discovered = false;
        void refreshGatewayModels();
    });
}

// ── Normalized capability calls (/v1/speech, /v1/images, /v1/vision) ──
// Speech, image generation and image understanding are one-shot calls with
// one thing to say and one thing to get back, so the gateway takes a single
// request shape and translates for whichever provider serves the role. The
// editor stops hand-assembling `contents[].parts[].inlineData` — Gemini's
// format — at four call sites, and a provider switch becomes a server-side
// table edit instead of an editor release.

/** Capability paths the gateway serves, under /v1/. */
export type CapabilityPath =
    | 'speech/transcribe'
    | 'speech/synthesize'
    | 'images/generate'
    | 'vision/analyze';

/**
 * Whether `role` is served on the normalized surface right now.
 *
 * False before discovery, like getGatewayRoute, and for a gateway that serves
 * the role on another surface.
 */
export function hasGatewayCapability(role: AIRole): boolean {
    return getGatewayRoute(role)?.surface === 'trace';
}

/**
 * The fields of a capability request as JSON, or, with files, as
 * `multipart/form-data`: the fields in a `json` part and each file as its own
 * part. The gateway refuses base64 in JSON, so bytes always go as files.
 */
export function capabilityBody(fields: Record<string, unknown>, files?: Record<string, Blob[]>): { headers: Record<string, string>; body: string | FormData } {
    if (!files) return { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields) };
    const form = new FormData();
    form.append('json', JSON.stringify(fields));
    for (const [name, list] of Object.entries(files)) {
        for (const file of list) form.append(name, file, name);
    }
    return { headers: {}, body: form };
}

/**
 * POST one capability request and answer its response, or throw with the
 * gateway's own message on failure so describeBillingError can still
 * recognize a 402.
 */
async function postCapability(
    path: CapabilityPath,
    fields: Record<string, unknown>,
    files: Record<string, Blob[]> | undefined,
    signal: AbortSignal | undefined,
): Promise<Response> {
    if (!AI_GATEWAY_URL) throw new Error('No AI gateway is configured.');
    const res = await gatewayFetch(`${AI_GATEWAY_URL}/v1/${path}`, { method: 'POST', ...capabilityBody(fields, files), signal });
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`Gateway ${res.status}: ${text.slice(0, 300) || res.statusText}`);
    }
    return res;
}

/** One capability request whose answer is JSON. */
export async function callCapability<T>(
    path: CapabilityPath,
    fields: Record<string, unknown>,
    /**
     * Forwarded to fetch so a cancelled call stops in flight. Without it a
     * capability the user aborts — a discarded TTS utterance, say — is still
     * fully fetched and still billed.
     */
    signal?: AbortSignal,
    /** Audio or pictures, by the part name the route takes them under. */
    files?: Record<string, Blob[]>,
): Promise<T> {
    return await (await postCapability(path, fields, files, signal)).json() as T;
}

/** One capability request whose answer is a file (a generated picture), typed by its content type. */
export async function callCapabilityFile(
    path: CapabilityPath,
    fields: Record<string, unknown>,
    signal?: AbortSignal,
): Promise<Blob> {
    return await (await postCapability(path, fields, undefined, signal)).blob();
}

// ── Documents for models (/v1/files) ────────────────────────
// The gateway takes an upload and says what a model can read of it; the model
// provider then reads the file itself, by the reference in `file`. Links are
// read by the gateway's own `fetch_url`. Shapes mirror llm-gateway src/documents.ts.

/** The file id in one of the gateway's own file URLs (`/files/<id>/<name>`), or null. */
export function storedFileId(url: string): string | null {
    return /\/files\/([0-9a-f]{32})(\/|$)/.exec(url)?.[1] ?? null;
}

/**
 * Whether `url` is a file this gateway stores, on its own origin; another site's `/files/…` path is not one. The
 * gateway keeps such a file only for a while, so a scene copies its bytes rather than linking to it.
 */
export function isGatewayFile(url: string): boolean {
    return AI_GATEWAY_URL !== null && url.startsWith(`${AI_GATEWAY_URL}/files/`) && storedFileId(url) !== null;
}

/** How the provider reaches the bytes: a URL it fetches, or a data: URL. */
export type GatewayFileRef = { url: string } | { data: string };

export interface GatewayPdfPreview { mime: 'image/png'; file: GatewayFileRef; width: number; height: number }

export interface GatewayPageLink {
    url: string;
    text: string;
    kind: 'pdf' | 'image' | 'page';
}

export type GatewayDocument =
    | {
        kind: 'pdf';
        /** The document's own URL — what the gateway's `fetch_url` takes to re-read or slice it. */
        url: string;
        filename: string;
        bytes: number;
        pageCount: number;
        /** 1-based selection `file` holds, e.g. "1-84"; empty when `file` is absent. */
        pages: string;
        region?: DocumentRegion;
        preview?: GatewayPdfPreview;
        /** Client-rendered bounded page selection, in source order, or zoomed areas in request order. */
        pagePreviews?: (GatewayPdfPreview & { page: number; region?: DocumentRegion })[];
        file?: GatewayFileRef;
        note?: string;
    }
    | { kind: 'image'; url: string; filename: string; mime: string; bytes: number; file: GatewayFileRef }
    | { kind: 'html'; url: string; title?: string; description?: string; text: string; truncated: boolean; links: GatewayPageLink[] }
    | { kind: 'text'; url: string; text: string; truncated: boolean };

async function documentOrThrow(res: Response): Promise<GatewayDocument> {
    if (!res.ok) {
        let message = '';
        try {
            message = ((await res.json()) as { message?: string }).message ?? '';
        } catch { /* not JSON */ }
        throw new Error(message || `Gateway ${res.status}: ${res.statusText}`);
    }
    return await res.json() as GatewayDocument;
}

/** Upload a file for a model; the gateway keeps it, and its `fetch_url` reads the answer's `url`. */
export async function uploadGatewayFile(file: Blob, filename: string, signal?: AbortSignal): Promise<GatewayDocument> {
    if (!AI_GATEWAY_URL) throw new Error('No AI gateway is configured.');
    return documentOrThrow(await gatewayFetch(`${AI_GATEWAY_URL}/v1/files`, {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-filename': encodeURIComponent(filename) },
        body: file,
        signal,
    }));
}

// ── 3D model generation (/v1/3d) ────────────────────────────
// The same normalized idea as the capability routes above, for work that
// does not finish inside the request: a generation runs 1–4 minutes, so the
// gateway hands back a job handle and this polls it. Which provider answers
// (Rodin today, TRELLIS.2 on our GPU stack later) is a server-side routing
// decision — nothing here names one.

export interface ModelGenRequest {
    /** What to build. Required unless `images` are supplied. */
    prompt?: string;
    /** Reference pictures, typed; sent as multipart file parts. */
    images?: Blob[];
    /** Default 'standard'. Buys latency, not cost — the provider charges a flat rate. */
    quality?: 'draft' | 'standard' | 'high';
    texture?: 'pbr' | 'shaded';
    topology?: 'triangles' | 'quads';
    /** Cap the result's proportions to a [width, height, length] box. */
    bounds?: [number, number, number];
}

export interface ModelGenAsset {
    kind: 'model' | 'preview';
    url: string;
    mimeType: string;
}

export interface ModelGenJob {
    jobId: string;
    status: 'pending' | 'succeeded' | 'failed';
    label: string;
    progress?: number;
    assets?: ModelGenAsset[];
    error?: string;
}

export interface ModelGenOptions {
    /** Called with a human-readable line as the job advances. */
    onProgress?: (message: string) => void;
    signal?: AbortSignal;
    /** Default 3s. The gateway meters job polls in their own rate bucket. */
    pollMs?: number;
    /** Default 8 minutes. */
    timeoutMs?: number;
}

export type ModelGenOutcome =
    | { blob: Blob; mimeType: string; label: string; elapsedMs: number }
    | { error: string };

/**
 * Generate a 3D model and return its bytes.
 *
 * Submit, poll, download — the whole job, because every caller wants the
 * finished mesh rather than a handle to babysit. The handle is stateless and
 * signed server-side, so a poll costs the gateway a provider status read and
 * nothing else.
 */
export async function generateModel3d(
    request: ModelGenRequest,
    options: ModelGenOptions = {},
): Promise<ModelGenOutcome> {
    if (!AI_GATEWAY_URL) return { error: 'No AI gateway is configured.' };
    const { onProgress, signal, pollMs = 3000, timeoutMs = GATEWAY_OPERATION_TIMEOUT_MS } = options;
    const started = Date.now();

    let job: ModelGenJob;
    try {
        const { images, ...fields } = request;
        const res = await gatewayFetch(`${AI_GATEWAY_URL}/v1/3d/generate`, {
            method: 'POST',
            ...capabilityBody(fields, images?.length ? { images } : undefined),
            signal,
        });
        const text = await res.text();
        if (!res.ok) return { error: `Gateway ${res.status}: ${text.slice(0, 300) || res.statusText}` };
        job = JSON.parse(text) as ModelGenJob;
    } catch (err) {
        return { error: err instanceof Error ? err.message : String(err) };
    }

    onProgress?.('3D generation queued — this usually takes 1–3 minutes.');

    while (job.status === 'pending') {
        if (signal?.aborted) return { error: '3D generation cancelled.' };
        if (Date.now() - started > timeoutMs) {
            return { error: `3D generation timed out after ${Math.round(timeoutMs / 1000)}s.` };
        }
        // Resolve early on abort so a cancel doesn't sit out the interval.
        await new Promise<void>(resolve => {
            const timer = setTimeout(resolve, pollMs);
            signal?.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
        });
        // Re-checked after the sleep, not just before it: an abort that lands
        // mid-interval would otherwise fall through to a fetch with an already
        // aborted signal and surface the platform's AbortError instead of the
        // message this function promises.
        if (signal?.aborted) return { error: '3D generation cancelled.' };
        try {
            const res = await gatewayFetch(`${AI_GATEWAY_URL}/v1/3d/jobs/${job.jobId}`, { signal });
            const text = await res.text();
            if (!res.ok) return { error: `Gateway ${res.status}: ${text.slice(0, 300) || res.statusText}` };
            job = JSON.parse(text) as ModelGenJob;
        } catch (err) {
            return { error: err instanceof Error ? err.message : String(err) };
        }
        onProgress?.(`generating 3D model… ${Math.round((Date.now() - started) / 1000)}s elapsed`);
    }

    if (job.status === 'failed') return { error: job.error ?? '3D generation failed.' };

    const model = job.assets?.find(a => a.kind === 'model');
    if (!model) return { error: 'The job finished but returned no model file.' };

    try {
        // Asset URLs are gateway-signed and still need the bearer.
        const res = await gatewayFetch(model.url, { signal });
        if (!res.ok) return { error: `Downloading the model failed (${res.status}).` };
        // A generation is a discrete, visible charge, and the readout must not
        // wait for the next turn to show it. Refreshing here rather than at a
        // call site covers every entry point — the agent loop, a slash
        // command, and anything added later — and by now the gateway's
        // settlement has had the whole download to land.
        void refreshCredits(true);
        return {
            blob: await res.blob(),
            mimeType: model.mimeType,
            label: job.label,
            elapsedMs: Date.now() - started,
        };
    } catch (err) {
        return { error: err instanceof Error ? err.message : String(err) };
    }
}

// ── Material generation (/v1/materials) ─────────────────────
// Prompt in, PBR material GLB out — albedo, a generated normal map and the
// metallic/roughness scalars, packed onto a unit plane so the same file
// previews anywhere and drops straight into a Renderer material slot.
//
// Unlike 3D generation this finishes inside the request (~20s), so there is
// no job handle to poll: one POST returns the bytes. The scalars the pipeline
// estimated come back as response *headers* rather than in the body, because
// the body is the GLB — see the gateway's route for why they are
// percent-encoded.

export interface MaterialGenRequest {
    /** The surface to synthesize, e.g. "weathered copper with verdigris". */
    prompt: string;
    /**
     * draft    — albedo + scalars only (one image call, ~3x faster).
     * standard — adds a generated normal map. The default.
     * high     — reserved for a larger image model; identical to standard today.
     */
    quality?: 'draft' | 'standard' | 'high';
    /** Texture edge length. 512 (default) or 1024. */
    resolution?: 512 | 1024;
}

/** What the pipeline estimated for the surface, from `x-material-properties`. */
export interface GeneratedMaterialProperties {
    metallic: number;
    roughness: number;
    normalStrength: number;
    /** The analysis model's one-line justification. */
    reasoning?: string;
}

export type MaterialGenOutcome =
    | {
        blob: Blob;
        /** Null when the header was absent or unparseable — never a reason to fail. */
        properties: GeneratedMaterialProperties | null;
        resolution: number | null;
        label: string;
        elapsedMs: number;
    }
    | { error: string };

/** Percent-encoded header (the gateway encodes because header values go out latin-1). */
function decodeHeader(res: Response, name: string): string | null {
    const raw = res.headers.get(name);
    if (raw === null) return null;
    try {
        return decodeURIComponent(raw);
    } catch {
        // A header that fails to decode is metadata, not the payload: fall
        // back to the raw value rather than losing a paid generation over it.
        return raw;
    }
}

/**
 * Generate a PBR material and return its GLB bytes.
 *
 * Degradation is the gateway's: an analysis failure still returns a material
 * (with default scalars), a depth failure still returns one (with no normal
 * map). Only a failed albedo is an error, so a non-ok status here means there
 * is genuinely nothing to apply.
 */
export async function generateMaterial(
    request: MaterialGenRequest,
    options: { signal?: AbortSignal } = {},
): Promise<MaterialGenOutcome> {
    if (!AI_GATEWAY_URL) return { error: 'No AI gateway is configured.' };
    const started = Date.now();
    try {
        const res = await gatewayFetch(`${AI_GATEWAY_URL}/v1/materials/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
            signal: options.signal,
        });
        if (!res.ok) {
            const text = await res.text().catch(() => '');
            return { error: `Gateway ${res.status}: ${text.slice(0, 300) || res.statusText}` };
        }

        const propertiesHeader = decodeHeader(res, 'x-material-properties');
        let properties: GeneratedMaterialProperties | null = null;
        if (propertiesHeader) {
            try {
                properties = JSON.parse(propertiesHeader) as GeneratedMaterialProperties;
            } catch {
                properties = null;
            }
        }
        const resolutionHeader = res.headers.get('x-material-resolution');
        const resolution = resolutionHeader ? Number(resolutionHeader) : null;

        const blob = await res.blob();
        // Same reasoning as generateModel3d: a generation is a visible charge,
        // so refresh the readout here rather than at each call site.
        void refreshCredits(true);
        return {
            blob,
            properties,
            resolution: Number.isFinite(resolution) ? resolution : null,
            label: decodeHeader(res, 'x-trace-label') ?? 'Trace AI · material',
            elapsedMs: Date.now() - started,
        };
    } catch (err) {
        return { error: err instanceof Error ? err.message : String(err) };
    }
}
